import {
  Component,
  computed,
  inject,
  input,
  signal,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { StrategyEx, StrategyGroupEx } from "@front/m19-models";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import { AccountSelectionService, AsinService, TacosStrategiesService } from "@front/m19-services";
import {
  IButtonDirective,
  ICheckboxComponent,
  ISelectMenuImports,
  ITooltipDirective,
  ModalRef,
  ModalService,
  ToasterService,
  IIconComponent,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { forkJoin, of, switchMap, tap } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { StrategyType } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import {
  ConfirmProductsDeletionModalComponent,
  ConfirmProductsDeletionModalData,
} from "@m19-board/sponsored-product/sp-page/strategy-group/confirm-products-deletion-modal/confirm-products-deletion-modal.component";

enum AddAsinsModalState {
  PRODUCTS,
  STRATEGY,
}

@Component({
  selector: "app-strategy-group-products",
  imports: [
    CommonModule,
    AsinSelectionComponent,
    TranslocoDirective,
    IButtonDirective,
    ITooltipDirective,
    ISelectMenuImports,
    ICheckboxComponent,
    IIconComponent,
  ],
  templateUrl: "./strategy-group-products.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyGroupProductsComponent {
  private readonly modalService = inject(ModalService);
  private readonly strategyService = inject(StrategyService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly asinService = inject(AsinService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);

  readonly AddAsinsModalState = AddAsinsModalState;

  @ViewChild("addAsinsModal") addAsinsModal!: TemplateRef<any>;
  addAsinModalRef?: ModalRef<any>;

  strategyGroup = input<StrategyGroupEx>();
  strategiesPerAsin = input<Map<string, StrategyEx[]>>();
  isReadOnly = input<boolean>(false);

  catalog = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((account) => this.asinService.getCatalog(account.accountId, account.marketplace)),
    ),
  );

  addAsinsModalState = signal<AddAsinsModalState>(AddAsinsModalState.PRODUCTS);
  addAsinsLoading = signal(false);
  selectedMainStrategy = signal<StrategyEx | undefined>(undefined);

  strategyGroupProductStrategies = computed(() => this.strategyGroup()?.productStrategies ?? []);
  strategyGroupFocusSelection: Set<number> = new Set();
  strategyGroupBrandSelection: Set<number> = new Set();

  strategyGroupAsins = computed(() => this.strategyGroup()?.asins ?? []);
  unavailableAsins = toSignal(this.tacosStrategiesService.unavailableAsinsForSponsoredAds$, {
    initialValue: new Set<string>(),
  });
  availableAsins = computed(() => {
    const unavailable = this.unavailableAsins();
    return Array.from(this.catalog()?.asinOffers.values() ?? [])
      .map((offer) => offer.asin)
      .filter((asin): asin is string => !!asin && !this.strategyGroupAsins().includes(asin) && !unavailable.has(asin));
  });

  selectedAsins = signal<string[]>([]);
  addAsinsSelected = signal<string[]>([]);

  deleteButtonLabel = computed(() =>
    this.translocoService.translate("common.delete_x_products", { count: this.selectedAsins().length }),
  );
  isAllAsinsSelected = computed(() => this.strategyGroupAsins().length === this.selectedAsins().length);

  deleteAsinLoading = signal(false);

  deleteProductsFromStrategy(toDelete: string[]) {
    if (!this.strategiesPerAsin() || toDelete.length === 0) return;

    const impactedStrats = this.getImpactedStrategies(toDelete, this.strategiesPerAsin()!);
    const modalRef = this.modalService.openModal<ConfirmProductsDeletionModalData, void>(
      ConfirmProductsDeletionModalComponent,
      {
        modalTitle: this.translocoService.translate("sp-strategy-group-page.product_deletion"),
        data: {
          productsToDelete: toDelete,
          impactedStrats: impactedStrats,
        },
      },
    );
    modalRef
      .afterClosed()
      .pipe(
        tap(() => this.deleteAsinLoading.set(true)),
        switchMap(() =>
          forkJoin(
            Array.from(impactedStrats.entries()).map(([strat, asins]) =>
              this.strategyService.deleteAsinsFromStrategy(strat, asins),
            ),
          ),
        ),
        // TODO: also stop strategies if no more ASINs
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("common.n_deleted_asins", { count: toDelete.length }),
          );
          this.deleteAsinLoading.set(false);
          this.selectedAsins.set([]);
        },
        error: (error) => {
          this.toastrService.error(
            this.translocoService.translate("sp-strategy-group-page.error_removing_products_from_strategy"),
            error,
          );
          this.deleteAsinLoading.set(false);
        },
      });
  }

  openAddAsinsModal() {
    this.selectedMainStrategy.set(this.strategyGroupProductStrategies()[0]);

    if (this.strategyGroup()?.keywordStrategies?.length) {
      this.strategyGroup()?.keywordStrategies.forEach((s) => this.strategyGroupFocusSelection.add(s.strategyId));
    }
    if (this.strategyGroup()?.brandStrategies?.length) {
      this.strategyGroup()?.brandStrategies.forEach((s) => this.strategyGroupBrandSelection.add(s.strategyId));
    }
    this.addAsinModalRef = this.modalService.openModal(this.addAsinsModal, {
      modalTitle: this.translocoService.translate("sp-substrategy.add_products"),
      size: "lg",
      height: "fill",
    });
    this.addAsinModalRef.afterCancelled().subscribe(() => {
      this.addAsinsSelected.set([]);
      this.addAsinsModalState.set(AddAsinsModalState.PRODUCTS);
    });
  }

  private getImpactedStrategies(toDelete: string[], strategiesPerAsin: Map<string, StrategyEx[]>) {
    return this.strategyService.getImpactedStrategiesOnDeleteProduct(toDelete, strategiesPerAsin);
  }

  addProducts() {
    const asinToAdd = this.addAsinsSelected();
    const mainStrategy = this.selectedMainStrategy();

    if (!mainStrategy) return;

    const focusStrategies = Array.from(this.strategyGroupFocusSelection)
      .map((id) => this.strategyGroup()?.keywordStrategies.find((s) => s.strategyId === id))
      .filter((s) => s !== undefined);

    const brandStrategies = Array.from(this.strategyGroupBrandSelection)
      .map((id) => this.strategyGroup()?.brandStrategies.find((s) => s.strategyId === id))
      .filter((s) => s !== undefined);

    this.addAsinsLoading.set(true);
    this.strategyService
      .addAsinsToStrategy(mainStrategy, asinToAdd)
      .pipe(
        switchMap((_) => {
          if (focusStrategies.length > 0 || brandStrategies.length > 0) {
            const focusAndBrandStrategiesCall = [...focusStrategies, ...brandStrategies].map((s) =>
              this.strategyService.addAsinsToStrategy(s!, asinToAdd),
            );
            return forkJoin(focusAndBrandStrategiesCall);
          }
          return of(null);
        }),
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("sp-strategy-group-page.products_added_to_strategy"),
          );
          this.closeAddProductsModal();
        },
        error: (error) => {
          this.toastrService.error(
            this.translocoService.translate("sp-strategy-group-page.error_adding_products_to_strategy"),
            error,
          );
        },
        complete: () => {
          this.addAsinsLoading.set(false);
        },
      });
  }

  closeAddProductsModal() {
    this.addAsinsSelected.set([]);
    this.addAsinModalRef?.close();
    this.addAsinsModalState.set(AddAsinsModalState.PRODUCTS);
  }

  toggleStrategyToSelection(strategy: StrategyEx, checked: boolean) {
    if (strategy.strategyType === StrategyType.KEYWORD) {
      if (checked) {
        this.strategyGroupFocusSelection.add(strategy.strategyId);
      } else {
        this.strategyGroupFocusSelection.delete(strategy.strategyId);
      }
    }
    if (strategy.strategyType === StrategyType.BRAND) {
      if (checked) {
        this.strategyGroupBrandSelection.add(strategy.strategyId);
      } else {
        this.strategyGroupBrandSelection.delete(strategy.strategyId);
      }
    }
  }
}
